import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Line } from '../objects/Line';
import { Segment } from '../objects/Segment';
import { Ray } from '../objects/Ray';
import { Arc } from '../objects/Arc';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

export class SegmentPrimitiveConstruction extends BaseConstruction {
  public buildSteps(options: { p1?: [number, number]; p2?: [number, number]; label?: string } = {}): ConstructionStep[] {
    const p1 = options.p1 ? { x: options.p1[0], y: options.p1[1] } : { x: 200, y: 400 };
    const p2 = options.p2 ? { x: options.p2[0], y: options.p2[1] } : { x: 550, y: 400 };

    const pointA = new Point(this.renderer, p1.x, p1.y, 'A');
    const pointB = new Point(this.renderer, p2.x, p2.y, 'B');
    const seg = new Segment(this.renderer, p1, p2, false);

    [pointA, pointB, seg].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Place Endpoints A and B',
        description: 'Locate endpoints A and B.',
        narration: 'Step 1: Locating segment endpoints A and B.',
        duration: 1000,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB)],
      },
      {
        id: 'step2',
        title: '2. Draw Segment AB with Ruler',
        description: 'Connect endpoints A and B using ruler.',
        narration: 'Step 2: Drawing segment AB with ruler.',
        duration: 1500,
        animations: [new DrawLine(seg, this.ruler), new Highlight(seg)],
      },
    ];
  }
}

export class PerpendicularLineOnLineConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 200;
    const startY = options.y ?? 450;
    const size = options.size ?? 350;
    const r = 70;

    const pA = { x: startX, y: startY };
    const pP = { x: startX + size / 2, y: startY }; // Point P on line
    const pB = { x: startX + size, y: startY };

    const pP1 = { x: pP.x - r, y: startY };
    const pP2 = { x: pP.x + r, y: startY };
    const pTop = { x: pP.x, y: startY - r * 1.5 };

    const pointP = new Point(this.renderer, pP.x, pP.y, 'P');
    const baseLine = new Segment(this.renderer, pA, pB, false);

    // arcLeft: center P, P1L is at 180°. CW 144°→216° = 72° arc ✓
    const arcLeft = new Arc(this.renderer, pP, r, Math.PI * 0.8, Math.PI * 1.2, true, true);
    // arcRight: center P, P1R is at 0°. CW -36°→36° = 72° arc ✓
    const arcRight = new Arc(this.renderer, pP, r, -Math.PI * 0.2, Math.PI * 0.2, true, true);

    // arcTop1: center P1L=(P.x-r), pTop is at ~-56°. CCW -36°→-81° = 45° arc ✓
    const arcTop1 = new Arc(this.renderer, pP1, r * 1.4, -Math.PI * 0.2, -Math.PI * 0.45, true, false);
    // arcTop2: center P1R=(P.x+r), pTop is at ~-124°. CCW -99°→-144° = 45° arc ✓
    const arcTop2 = new Arc(this.renderer, pP2, r * 1.4, -Math.PI * 0.55, -Math.PI * 0.8, true, false);

    const perpLine = new Segment(this.renderer, { x: pP.x, y: pP.y + 40 }, pTop, false);

    [pointP, baseLine, arcLeft, arcRight, arcTop1, arcTop2, perpLine].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Baseline and Point P',
        description: 'Given baseline and point P on the line.',
        narration: 'Step 1: Given baseline and point P on the line.',
        duration: 1200,
        animations: [new DrawPoint(pointP), new DrawLine(baseLine, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Mark Equal Arcs on Either Side of P',
        description: 'Draw arcs centered at P to mark equidistant points P1 and P2.',
        narration: 'Step 2: Marking equal arcs on either side of point P.',
        duration: 1800,
        animations: [
          new MoveCompass(this.compass, pP, r),
          new DrawArc(arcLeft, this.compass),
          new DrawArc(arcRight, this.compass),
        ],
      },
      {
        id: 'step3',
        title: '3. Intersect Arcs Above P',
        description: 'Draw intersecting arcs from P1 and P2 above line.',
        narration: 'Step 3: Drawing intersecting arcs above point P.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pP1, r * 1.4),
          new DrawArc(arcTop1, this.compass),
          new MoveCompass(this.compass, pP2, r * 1.4),
          new DrawArc(arcTop2, this.compass),
        ],
      },
      {
        id: 'step4',
        title: '4. Draw Perpendicular Line through P',
        description: 'Using ruler, draw perpendicular line passing through P.',
        narration: 'Construction complete! Perpendicular line drawn through point P.',
        duration: 1500,
        animations: [new Fade(this.compass, false), new DrawLine(perpLine, this.ruler), new Highlight(perpLine)],
      },
    ];
  }
}
